import { QueryClient } from '@tanstack/react-query';
import {
  ConversationMessageEntity,
  ConversationMessageWithStatus,
} from '../../types/conversation.types';
import {
  hasChatMessageByClientId,
  hasChatMessagesCache,
  patchChatMessage,
  removeChatMessageByClientId,
  upsertChatMessage,
} from './chatCache';

/**
 * Viktor's streamed answer, as a row in the thread cache.
 *
 * While Viktor writes, the answer exists only here: a cache-only row with the
 * `id: 0` placeholder every not-yet-stored row carries, `streaming: true`, and
 * a `clientMessageId` derived from the run - which is what every reducer below
 * finds it by, the same way an optimistic send is found by its own
 * `clientMessageId`. The stored message then REPLACES the row through the
 * run id it carries in `systemMeta`, so the bubble the answer was written into
 * is the bubble it ends up in, with no jump.
 *
 * Every reducer is a no-op when the thread has never been loaded: the socket
 * delivers Viktor's events wherever the user is in the app, and creating a
 * partial first page for a thread that is not open would paginate wrongly the
 * moment it is opened - the same rule `useConversationsSocket` applies to
 * `new_message`. The answer is not lost, it is fetched with the history.
 */

/** The run's slot in the cache. Never sent anywhere. */
export const viktorRunClientId = (runId: string) => `viktor-run:${runId}`;

/** The stored answer that closes a run - see `resolveViktorRun`. */
export const viktorRunIdOf = (message: ConversationMessageEntity) =>
  message.side === 'viktor' ? message.systemMeta?.viktorRunId ?? null : null;

const streamingRow = (
  conversationId: number,
  runId: string,
  body: string
): ConversationMessageWithStatus => ({
  id: 0,
  conversationId,
  side: 'viktor',
  sender: null,
  /* Written by an AI by definition. The flag is what a merchant thread needs
     to tell the bot from a human agent; here `side` already says it, and the
     row still has to carry it honestly. */
  isAssistant: true,
  body,
  attachments: [],
  orderId: null,
  replyTo: null,
  clientMessageId: viktorRunClientId(runId),
  systemEvent: null,
  systemMeta: { viktorRunId: runId },
  mentionUserIds: [],
  seen: true,
  editedAt: null,
  deletedAt: null,
  createdAt: new Date().toISOString(),
  // The triage bot's cards belong to merchant support threads, never here.
  actionProposal: null,
  ticket: null,
  quickReplies: null,
  streaming: true,
});

/** `viktor_run_started`: the empty bubble the indicator lives in. */
export const startViktorRun = (
  queryClient: QueryClient,
  conversationId: number,
  runId: string
) => {
  if (!hasChatMessagesCache(queryClient, conversationId)) return;
  upsertChatMessage(
    queryClient,
    conversationId,
    streamingRow(conversationId, runId, '')
  );
};

/**
 * `viktor_delta`: grow the bubble by `text`.
 *
 * Creates the row when it is missing rather than dropping the delta: the
 * thread may have been opened - or refetched after a reconnect - while the run
 * was already under way, and the text from here on is better than a bubble
 * that only appears once the answer is finished.
 */
export const appendViktorDelta = (
  queryClient: QueryClient,
  conversationId: number,
  runId: string,
  text: string
) => {
  if (!hasChatMessagesCache(queryClient, conversationId)) return;
  const clientMessageId = viktorRunClientId(runId);
  const grown = patchChatMessage(
    queryClient,
    conversationId,
    clientMessageId,
    (row) => ({ ...row, body: (row.body ?? '') + text })
  );
  if (!grown) {
    upsertChatMessage(
      queryClient,
      conversationId,
      streamingRow(conversationId, runId, text)
    );
  }
};

/**
 * `new_message` with `side: 'viktor'`: the stored answer takes the streamed
 * bubble's place. Matched on the run id the message carries, since its
 * `clientMessageId` is the server's own and never equals the slot's.
 *
 * Falls back to a plain upsert when no slot exists for the run - the answer
 * was written while this tab was elsewhere, and the history it is being added
 * to has no bubble to replace.
 */
export const resolveViktorRun = (
  queryClient: QueryClient,
  conversationId: number,
  message: ConversationMessageEntity
) => {
  const runId = viktorRunIdOf(message);
  if (runId !== null) {
    const replaced = patchChatMessage(
      queryClient,
      conversationId,
      viktorRunClientId(runId),
      () => message
    );
    if (replaced) return;
  }
  upsertChatMessage(queryClient, conversationId, message);
};

/**
 * Take the streamed bubble out: the run was cancelled (nothing was stored), or
 * its stored message never reached this tab and the history is about to be
 * refetched instead.
 */
export const discardViktorRun = (
  queryClient: QueryClient,
  conversationId: number,
  runId: string
) =>
  removeChatMessageByClientId(
    queryClient,
    conversationId,
    viktorRunClientId(runId)
  );

/** Whether the run still has a bubble in the cache. */
export const hasViktorRunRow = (
  queryClient: QueryClient,
  conversationId: number,
  runId: string
) =>
  hasChatMessageByClientId(
    queryClient,
    conversationId,
    viktorRunClientId(runId)
  );
